import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import Decimal from 'decimal.js';
import isEmpty from 'lodash.isempty';
import { useEffect, useState } from 'react';

import { useNotificationStore } from '../../stores/notificationStore';
import { useUserStore } from '../../stores/userStore';
import { getFinanceForms, getLastResponses, saveFinanceValues } from './api';

const PersonalFinances = () => {
	const { showSuccess, showError } = useNotificationStore();
	const { user } = useUserStore();
	const queryClient = useQueryClient();
	const [incomes, setIncomes] = useState([]);
	const [expenses, setExpenses] = useState([]);
	const [lastIncomeEnteredAt, setLastIncomeEnteredAt] = useState(null);
	const [lastExpenseEnteredAt, setLastExpenseEnteredAt] = useState(null);

	const { data: groups, isPending: isGroupsPending } = useQuery({
		queryKey: ['personalFinanceGroups'],
		queryFn: getFinanceForms,
	});
	const groupIds = groups?.map(({ id }) => id);
	const { data: previousResponses, isPending: isPreviousResponsesPending } =
		useQuery({
			queryKey: ['previousResponses', groupIds, user],
			queryFn: () => getLastResponses(groupIds, user.id),
			enabled: Array.isArray(groups),
		});
	const assessmentMutation = useMutation({
		mutationFn: saveFinanceValues,
		onSuccess: () => {
			showSuccess('Financial data saved');
			queryClient.invalidateQueries({ queryKey: ['previousResponses'] });
		},
		onError: () => {
			showError(
				'There was a problem saving your financial data. Please try again.',
			);
		},
	});

	useEffect(() => {
		if (Array.isArray(groups) && groups.length > 0) {
			setIncomes(
				groups[0].care_quest_group_questions.map(
					({ default_answer }) => default_answer,
				),
			);
			setExpenses(
				groups[1].care_quest_group_questions.map(
					({ default_answer }) => default_answer,
				),
			);
		}
	}, [groups]);
	useEffect(() => {
		if (Array.isArray(previousResponses) && previousResponses.length > 0) {
			// incomes
			const incomeResponse = previousResponses.find(
				({ care_quest_groupId }) => care_quest_groupId === groupIds[0],
			);
			if (
				incomeResponse &&
				Array.isArray(incomeResponse.care_quest_responses) &&
				incomeResponse.care_quest_responses.length > 0
			) {
				const questionIds = groups[0].care_quest_group_questions.map(
					({ id }) => id,
				);
				const previousIncomes = [];
				questionIds.forEach((questionId) => {
					previousIncomes.push(
						incomeResponse.care_quest_responses.find(
							({ care_quest_questionId }) =>
								care_quest_questionId === questionId,
						),
					);
				});
				setIncomes(
					previousIncomes.map(
						({ care_quest_answer_text }) => care_quest_answer_text,
					),
				);

				setLastIncomeEnteredAt(incomeResponse.response_dttm);
			}
			// expenses
			const expenseResponse = previousResponses.find(
				({ care_quest_groupId }) => care_quest_groupId === groupIds[1],
			);
			if (
				expenseResponse &&
				Array.isArray(expenseResponse.care_quest_responses) &&
				expenseResponse.care_quest_responses.length > 0
			) {
				const questionIds = groups[1].care_quest_group_questions.map(
					({ id }) => id,
				);
				const previousExpenses = [];
				questionIds.forEach((questionId) => {
					previousExpenses.push(
						expenseResponse.care_quest_responses.find(
							({ care_quest_questionId }) =>
								care_quest_questionId === questionId,
						),
					);
				});
				setExpenses(
					previousExpenses.map(
						({ care_quest_answer_text }) => care_quest_answer_text,
					),
				);
				setLastExpenseEnteredAt(expenseResponse.response_dttm);
			}
		}
	}, [previousResponses]);

	const handleIncomeEntry = (value, index) => {
		const newIncomes = Array.from(incomes);
		newIncomes[index] = value;
		setIncomes(newIncomes);
	};

	const handleExpenseEntry = (value, index) => {
		const newExpenses = Array.from(expenses);
		newExpenses[index] = value;
		setExpenses(newExpenses);
	};

	const totalIncome = incomes.reduce((partialSum, a, currIndex) => {
		if (!isEmpty(a) && currIndex < incomes.length - 1) {
			try {
				return Decimal.add(a, partialSum);
			} catch (err) {
				// console.log(err);
			}
		}
		return partialSum;
	}, 0);
	const totalExpense = expenses.reduce((partialSum, a, currIndex) => {
		if (!isEmpty(a) && currIndex < expenses.length - 1) {
			try {
				return Decimal.add(a, partialSum);
			} catch (err) {
				// console.log(err);
			}
		}
		return partialSum;
	}, 0);

	const handleSaveFinancialAssessment = ({ target }) => {
		const isIncomeForm = target.value == groupIds[0];
		const care_quest_groupId = target.value;
		const questions = groups.find(
			({ id }) => id == care_quest_groupId,
		).care_quest_group_questions;
		const answers = Array.from(isIncomeForm ? incomes : expenses);
		answers.splice(
			answers.length - 1,
			1,
			isIncomeForm ? totalIncome : totalExpense,
		);
		assessmentMutation.mutate({
			userId: user.id,
			care_quest_groupId,
			questions,
			answers,
		});
	};

	return (
		<div className="personal-finances">
			<div className="page-wide">
				<h1>Personal Finances</h1>
				<div className="card card-bordered">
					<div className="card-title">
						Income{' '}
						{lastIncomeEnteredAt && (
							<em>
								<small className="font-normal text-gray-400 align-text-top">
									Last updated on{' '}
									{format(new Date(lastIncomeEnteredAt), 'PPPp')}
								</small>
							</em>
						)}
					</div>
					<div className="card-body">
						<div>
							{groups &&
								groups[0].care_quest_group_questions
									.filter(({ question_type }) => question_type === 'Numeric')
									.map((question, index) => (
										<div
											key={`question-${question.id}`}
											className="relative sm:col-span-2"
										>
											<label htmlFor={`income-${question.id}`}>
												{question.question_text}
											</label>
											<div>
												<span>$</span>
											</div>
											<input
												type="number"
												min={0.0}
												step={0.01}
												inputMode="decimal"
												id={`income-${question.id}`}
												name={question.id}
												placeholder={question.default_answer}
												value={incomes[index]}
												onChange={({ target }) =>
													handleIncomeEntry(target.value, index)
												}
											/>
										</div>
									))}
						</div>
					</div>
					<div className="flex flex-row card-action">
						<div className="basis-3/4">
							Total: ${totalIncome.toString()} per month
						</div>
						<div className="basis-1/4">
							{Array.isArray(groups) && (
								<button
									className="text-white btn btn-primary btn-block"
									name="care_quest_groupId"
									value={groupIds[0]}
									disabled={
										isGroupsPending ||
										isPreviousResponsesPending ||
										assessmentMutation.isPending
									}
									onClick={handleSaveFinancialAssessment}
								>
									Save Income
								</button>
							)}
						</div>
					</div>
				</div>
				<div className="card card-bordered">
					<div className="card-title">
						Expenses{' '}
						{lastExpenseEnteredAt && (
							<em>
								<small className="font-normal text-gray-400 align-text-top">
									Last updated on{' '}
									{format(new Date(lastExpenseEnteredAt), 'PPPp')}
								</small>
							</em>
						)}
					</div>
					<div className="card-body">
						<div>
							{groups &&
								groups[1].care_quest_group_questions
									.filter(({ question_type }) => question_type === 'Numeric')
									.map((question, index) => (
										<div
											key={`question-${question.id}`}
											className="relative sm:col-span-2"
										>
											<label htmlFor={`expense-${question.id}`}>
												{question.question_text}
											</label>
											<div>
												<span>$</span>
											</div>
											<input
												type="number"
												min={0.0}
												step={0.01}
												inputMode="decimal"
												id={`expense-${question.id}`}
												placeholder={question.default_answer}
												onChange={({ target }) =>
													handleExpenseEntry(target.value, index)
												}
												name={question.id}
												value={expenses[index]}
											/>
										</div>
									))}
						</div>
					</div>
					<div className="flex flex-row card-action">
						<div className="basis-3/4">
							Total: ${totalExpense.toString()} per month
						</div>
						<div className="basis-1/4">
							{Array.isArray(groups) && (
								<button
									className="text-white btn btn-primary btn-block"
									name="care_quest_groupId"
									value={groupIds[1]}
									disabled={
										isGroupsPending ||
										// isQuestionsPending ||
										isPreviousResponsesPending ||
										assessmentMutation.isPending
									}
									onClick={handleSaveFinancialAssessment}
								>
									Save Expenses
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PersonalFinances;
